import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { crud } from '@/common/crud';
import { useAuthFlow } from '@/common/workflowEngine/useAuthFlow';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { ChargeItem, HealthcareService, Reference } from '@/fhirworks';
import { differenceInMinutes } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

const { postTransaction } = useWorkflow();

// UTILITY FUNCTIONS
const processFhirBundleResponse = async function (url) {
    let result = null;
    await $httpFhirApi.get(url).then((response) => {
        if (response.data && response.data.entry && response.data.entry.length > 0) {
            result = response.data.entry.map((entry) => entry.resource);
        } else if (response.data && Array.isArray(response.data)) {
            result = response.data;
        } else if (response.data) {
            result = [response.data];
        }
    });
    return result;
};

const calculateEncounterDuration = function (encounter) {
    if (!encounter?.period?.start || !encounter?.period?.end) return 0;

    const startTime = new Date(encounter.period.start);
    const endTime = new Date(encounter.period.end);
    return differenceInMinutes(endTime, startTime);
};

const normalizeDecimalValue = function (value, fallback = 0) {
    if (value === null || value === undefined || value === '') {
        return fallback;
    }

    const numericValue = typeof value === 'number' ? value : parseFloat(value);

    return Number.isNaN(numericValue) ? fallback : numericValue;
};

const getPerUnitChargeValue = function (charge, healthcareService, fallback = 0) {
    const rawValue = charge?.fixedTimeDuration?.perUnitCharge ?? healthcareService?.fixedTimeDuration?.perUnitCharge ?? healthcareService?.baseCharge ?? fallback;

    return normalizeDecimalValue(rawValue, fallback);
};

const calculateUnitsForDuration = function (encounterDurationMinutes, perUnitDuration, maxUnitAllowed = Infinity) {
    const durationValue = encounterDurationMinutes?.value ?? encounterDurationMinutes;

    if (durationValue <= 0 || !perUnitDuration || perUnitDuration <= 0) return 1;

    const calculatedUnits = Math.ceil(durationValue / perUnitDuration);
    const result = calculatedUnits > maxUnitAllowed ? maxUnitAllowed : calculatedUnits;

    return result;
};

const getPerUnitDurationAndMaxUnits = function (hasCoverage, charge, healthcareService) {
    let perUnitDuration;
    let maxUnitAllowed;

    if (hasCoverage) {
        perUnitDuration = charge?.fixedTimeDuration?.perUnitDuration;
        maxUnitAllowed = charge?.fixedTimeDuration?.maxUnitsAllowed;
    } else {
        perUnitDuration = healthcareService.fixedTimeDuration?.perUnitDuration;
        maxUnitAllowed = healthcareService.fixedTimeDuration?.maxUnitsAllowed;
    }

    return { perUnitDuration, maxUnitAllowed };
};

const findThresholdForDuration = function (sortedThresholds, encounterDurationMinutes) {
    if (encounterDurationMinutes <= 0) return null;

    // Find a threshold where duration falls between min and max
    let selectedThreshold = sortedThresholds.find((threshold) => encounterDurationMinutes >= (threshold.min || 0) && encounterDurationMinutes <= (threshold.max || Infinity));

    // Handle edge cases if no matching range found
    if (!selectedThreshold) {
        // If duration is below the minimum threshold's min value, use the first threshold
        if (encounterDurationMinutes < (sortedThresholds[0].min || 0)) {
            selectedThreshold = sortedThresholds[0];
        }
        // If duration exceeds the last threshold's max value, use the last threshold
        else if (encounterDurationMinutes > (sortedThresholds[sortedThresholds.length - 1].max || Infinity)) {
            selectedThreshold = sortedThresholds[sortedThresholds.length - 1];
        }
    }

    // If we still couldn't find a matching threshold, use the first one as fallback
    if (!selectedThreshold && sortedThresholds.length > 0) {
        selectedThreshold = sortedThresholds[0];
    }

    return selectedThreshold;
};

const getModifiersFromProcedure = function (procedure) {
    if (!procedure?.modifier?.length) return null;

    return procedure.modifier
        .sort((a, b) => (a.position > b.position ? 1 : a.position < b.position ? -1 : a.code > b.code ? 1 : a.code < b.code ? -1 : 0))
        .map((m) => m.code)
        .join(', ');
};

const createPatientSpecificReferences = function (references, patient, encounter) {
    return {
        ...references,
        patient: new Reference({
            id: patient.id,
            resourceType: 'Patient',
            display: patient.fullName || 'Patient',
        }),
        encounter: new Reference({
            id: encounter.id,
            resourceType: 'Encounter',
        }),
    };
};

const createCommonReferences = function (data) {
    // Create a Reference to the Patient
    const patientReference = new Reference({
        id: data.activePatient.id,
        resourceType: 'Patient',
        display:
            data.activePatient.name && data.activePatient.name[0]
                ? `${data.activePatient.name[0].given.join(' ')} ${data.activePatient.name[0].family}`
                : data.activePatient.fullName || 'Unknown Patient',
    });

    // Create a Reference to the Encounter
    const encounterReference = new Reference({
        id: data.activeEncounter.id,
        resourceType: 'Encounter',
    });

    // Create a Reference to the Provider
    const providerReference = new Reference({
        id: data.actor.id,
        resourceType: 'Practitioner',
        display: data.actor.name ? `${data.actor.name[0].given.join(' ')} ${data.actor.name[0].family}` : data.actor.fullName || 'Unknown Provider',
    });

    // QuestionnaireResponse reference if available
    let questionnaireResponseReference = null;

    // Check if questionnaire response data is available
    let questionnaireResponseData = null;

    if (data.questionnaireResponses && Array.isArray(data.questionnaireResponses) && data.questionnaireResponses.length > 0) {
        questionnaireResponseData = data.questionnaireResponses;
    } else if (data.questionnaireResponse) {
        // Handle single questionnaire response
        questionnaireResponseData = [data.questionnaireResponse];
    }

    if (questionnaireResponseData && questionnaireResponseData.length > 0) {
        const qr = questionnaireResponseData[0];
        questionnaireResponseReference = new Reference({
            id: qr.id,
            resourceType: 'QuestionnaireResponse',
        });
    }

    const references = {
        patient: patientReference,
        encounter: encounterReference,
        provider: providerReference,
        questionnaireResponse: questionnaireResponseReference,
    };

    return references;
};

// DATA FETCHING FUNCTIONS
const fetchPatientCoverage = async function (patientId) {
    let coverages = [];

    await $httpFhirApi
        .get(`Coverage?beneficiary=${patientId}&status=active`)
        .then((response) => {
            // Extract the coverage from response
            if (response.data && response.data.entry && response.data.entry.length > 0) {
                coverages = response.data.entry.map((entry) => entry.resource);
            } else if (response.data && Array.isArray(response.data)) {
                coverages = response.data;
            } else if (response.data) {
                coverages = [response.data];
            }
        })
        .catch((error) => {
            // Error fetching coverage for patient
        });

    if (coverages.length > 0) {
        // First look for primary coverage
        const primaryCoverage = coverages.find((coverage) => coverage.insurancePriority?.coding?.[0]?.code === 'P');

        // Return primary if found, otherwise the first coverage
        const result = primaryCoverage || coverages[0];
        return result;
    }
    return null;
};

const fetchPatientFeeSchedule = async function (coverage) {
    if (!coverage || !coverage.payor || !coverage.payor[0]?.display) {
        return null;
    }

    const result = await processFhirBundleResponse(`BN_FeeSchedule?payers-display=${coverage.payor[0].display}`);
    const filteredResult = result.filter((item) => item.active === true);
    return filteredResult;
};

const fetchHealthcareService = async function (billingCode) {
    let healthcareService = null;

    await $httpFhirApi.get(`HealthcareService?billingCode=${billingCode}`).then((response) => {
        // Extract the healthcare service from response
        if (response.data && response.data.entry && response.data.entry.length > 0) {
            healthcareService = new HealthcareService(response.data.entry[0].resource);
        } else if (response.data && response.data.length > 0) {
            healthcareService = new HealthcareService(response.data[0]);
        }
    });
    return healthcareService;
};

const fetchPatientProcedureFromDB = async function (data, patientId, billingCode) {
    try {
        let dbProcedures = await processFhirBundleResponse(`Procedure?subject=${patientId}&code=${billingCode}`);
        dbProcedures = dbProcedures || [];

        if (dbProcedures.length > 0) {
            // First try to find one that matches the active encounter
            let matchingProcedure = dbProcedures.find((proc) => proc.context?.id === data.activeEncounter?.id || proc.encounter?.id === data.activeEncounter?.id);

            if (matchingProcedure) {
                return matchingProcedure;
            }

            // If no specific encounter match, return the first procedure found
            return dbProcedures[0];
        }
        return null;
    } catch (error) {
        // Error fetching procedure from database for patient
        return null;
    }
};

const findProcedureByBillingCode = async function (data, billingCode) {
    // First check local procedures (already loaded from data provider)
    if (data.activePatientProcedures?.length) {
        const match = data.activePatientProcedures.find(
            (proc) => proc.code?.coding?.some((coding) => coding.code === billingCode) && (proc.encounter?.id === data.activeEncounter?.id || proc.context?.id === data.activeEncounter?.id),
        );
        if (match) return match;
    }

    // Fallback to API if no local match
    try {
        const procedures = await processFhirBundleResponse(`Procedure?code=${billingCode}&encounter=${data.activeEncounter.id}`);
        if (procedures?.length) {
            return procedures.find((proc) => proc.subject?.id === data.activePatient?.id) || procedures[0];
        }
    } catch {
        // API failed, already checked local data
    }

    return null;
};

// CORE PROCESSING FUNCTIONS
const processAddOnCodes = async function ({ healthcareService, encounterDurationMinutes, units, threshold, references, patientId = null }, data, transaction) {
    // Always use healthcareService for add-on triggers, not the charge object
    // For group encounters, check if we've already made a decision for this service
    if (shouldProcessGroupEncounter(data)) {
        const serviceKey = healthcareService.id;
        if (data.groupAddOnDecisions && data.groupAddOnDecisions.has(serviceKey)) {
            const addOnCodes = data.groupAddOnDecisions.get(serviceKey);
            if (addOnCodes && addOnCodes.length > 0) {
                return await createAddOnChargeItems(addOnCodes, healthcareService, references, transaction);
            }
            return transaction;
        }
    }

    const addOnCodes = patientId
        ? await data.checkForAddOnTriggersOnce(patientId, healthcareService, encounterDurationMinutes, units, threshold)
        : await data.checkForAddOnTriggers(healthcareService, encounterDurationMinutes, units, threshold);

    // For group encounters, store the decision for reuse by all patients
    if (shouldProcessGroupEncounter(data)) {
        if (!data.groupAddOnDecisions) data.groupAddOnDecisions = new Map();
        const serviceKey = healthcareService.id;
        data.groupAddOnDecisions.set(serviceKey, addOnCodes);
    }

    // If add-on codes were confirmed by the user, process them
    if (addOnCodes && addOnCodes.length > 0) {
        // Process each add-on code
        for (const addOnCode of addOnCodes) {
            // Create charge item for the add-on using the original service info but with the add-on code
            transaction = await createAndSaveChargeItem(
                {
                    chargeData: {
                        code: addOnCode,
                        display: `${healthcareService.name} - Add-on (${addOnCode})`,
                        price: healthcareService.baseCharge || 0,
                        status: 'planned',
                    },
                    references,
                    relatedProcedure: null, // No related procedure for add-ons
                    isAddOn: true, // This is an add-on code
                    healthcareService,
                },
                transaction,
            );
        }
    }

    return transaction;
};

const processFeeSchedules = async function (data, patientFeeSchedules = null, existingTransaction = false) {
    // Use patientFeeSchedules if provided (for group encounters), otherwise use data.feeScheduleData
    const schedulesToProcess = patientFeeSchedules || data.feeScheduleData;
    let transaction = existingTransaction || [];
    let chargesProcessed = 0;

    if (!(schedulesToProcess?.length > 0 && data.activeEncounter?.id && data.activePatient?.id)) {
        return { transaction, chargesProcessed };
    }
    // Loop through each fee schedule
    for (const feeSchedule of schedulesToProcess) {
        if (!feeSchedule.charges || feeSchedule.charges.length === 0) {
            continue;
        }

        // Process each charge in the fee schedule
        for (const charge of feeSchedule.charges) {
            // Fetch the HealthcareService based on charge.billingCode
            if (!charge.billingCode) {
                continue;
            }

            const healthcareService = await fetchHealthcareService(charge.billingCode);
            if (!healthcareService) {
                continue;
            }

            // Only process if the encounter has a procedure with a matching billing code
            const relatedProcedure = await findProcedureByBillingCode(data, healthcareService.billingCode);
            if (!relatedProcedure) {
                // No matching procedure for this encounter - skip this fee schedule charge
                continue;
            }

            // Create common references once to reduce repetition
            const references = createCommonReferences(data);

            // Process by healthcare service type
            if (healthcareService.fixedTimeDuration && healthcareService.fixedTimeDuration.perUnitCharge) {
                transaction = await processFixedTimeDuration(data, healthcareService, references, relatedProcedure, charge, transaction);
                chargesProcessed++;
            } else if (healthcareService.thresholds && healthcareService.thresholds.length > 0) {
                transaction = await processThresholds(data, healthcareService, references, relatedProcedure, charge, transaction);
                chargesProcessed++;
            }
        }
    }

    return { transaction, chargesProcessed };
};

// Process HealthcareService objects directly (when no fee schedule)
const processHealthcareServices = async function (data, healthcareServices = [], existingTransaction = false) {
    let transaction = existingTransaction || [];

    if (!(healthcareServices.length > 0 && data.activeEncounter?.id && data.activePatient?.id)) {
        return transaction;
    }

    // Process each healthcare service directly
    for (const healthcareService of healthcareServices) {
        // Create common references once to reduce repetition
        const references = createCommonReferences(data);

        // Find related procedure if we have a billing code
        let relatedProcedure = null;
        if (healthcareService.billingCode) {
            relatedProcedure = await findProcedureByBillingCode(data, healthcareService.billingCode);
        }

        // Process by healthcare service type
        if (healthcareService?.fixedTimeDuration?.perUnitCharge || (healthcareService.isAddOn && healthcareService.baseCharge)) {
            transaction = await processFixedTimeDuration(data, healthcareService, references, relatedProcedure, healthcareService, transaction);
        } else if (healthcareService.thresholds && healthcareService.thresholds.length > 0) {
            transaction = await processThresholds(data, healthcareService, references, relatedProcedure, healthcareService, transaction);
        }
    }

    return transaction;
};

const processPatientFeeSchedule = async function (data, patientId, feeSchedules, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Find the patient's encounter in participantEncounters
    const updatedEncounter = data.participantEncounters[patientId];
    if (!updatedEncounter) {
        return transaction;
    }

    // Create patient-specific data for fee schedule processing
    const patientData = {
        ...data,
        activePatient: data.patients.find((p) => p.id === patientId),
        activeEncounter: updatedEncounter,
        // Keep group encounter context for consistent processing
        isGroupEncounter: data.isGroupEncounter,
        // Set encounterDurationMinutes for single patient processing
        encounterDurationMinutes: calculateEncounterDuration(updatedEncounter),
        // Ensure checkForAddOnTriggersOnce is available
        checkForAddOnTriggersOnce: data.checkForAddOnTriggersOnce,
    };

    // Pass patientData to maintain correct patient context while preserving group encounter context
    const result = await processFeeSchedules(patientData, feeSchedules, transaction);
    transaction = result.transaction;

    return { transaction, chargesProcessed: result.chargesProcessed };
};

const processPatientHealthcareServices = async function (data, patientId, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Find the patient's encounter in participantEncounters
    const updatedEncounter = data.participantEncounters[patientId];
    if (!updatedEncounter) {
        return transaction;
    }

    // Create patient-specific data for healthcare services processing
    const patientData = {
        ...data,
        activePatient: data.patients.find((p) => p.id === patientId),
        activeEncounter: updatedEncounter,
        // Keep group encounter context for threshold processing
        isGroupEncounter: data.isGroupEncounter,
        // Set encounterDurationMinutes for single patient processing
        encounterDurationMinutes: calculateEncounterDuration(updatedEncounter),
        // Ensure checkForAddOnTriggersOnce is available
        checkForAddOnTriggersOnce: data.checkForAddOnTriggersOnce,
    };

    // Pass patientData to maintain correct patient context while preserving group encounter context
    transaction = await processHealthcareServices(patientData, data.updatedServices, transaction);

    return transaction;
};

// Unified function to process a threshold (works for both single patient and group patient)
const createThresholdChargeItem = async function ({ threshold, healthcareService, charge, relatedProcedure, references, encounterDurationMinutes }, data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Find matching healthcare service threshold to get the charge value
    const healthcareServiceThreshold = healthcareService.thresholds.find((t) => (t.min ?? -1) === (threshold.min ?? -1) && (t.max ?? Infinity) === (threshold.max ?? Infinity));

    if (!healthcareServiceThreshold?.charge) {
        return transaction;
    }

    // Get properties from charge threshold with fallback to healthcareService threshold
    const units = threshold.units || healthcareServiceThreshold.units || 1;
    const procedureCodeOverride = threshold.procedureCodeOverride || healthcareServiceThreshold.procedureCodeOverride;
    // Determine which billing code to use
    const procedureCode = procedureCodeOverride || charge?.billingCode || healthcareService.billingCode;

    // Update procedure if needed using helper function
    await updateProcedureIfNeeded(relatedProcedure, procedureCodeOverride, units, transaction);

    // Fetch patient coverage for this charge item
    const patientCoverage = await fetchPatientCoverage(references.patient.id);

    // FIRST: Create and save the main charge item
    transaction = await createAndSaveChargeItem(
        {
            chargeData: {
                code: procedureCode,
                display: charge?.name || healthcareService.name,
                price: healthcareServiceThreshold.charge, // Always use charge from healthcare service threshold
                status: charge?.status || 'planned',
                dateOfService: new Date().toISOString(),
                coverage: patientCoverage
                    ? {
                          id: patientCoverage.id,
                          resourceType: 'Coverage',
                          display: patientCoverage.payor?.[0]?.display || 'Unknown Payer',
                      }
                    : undefined,
            },
            references,
            relatedProcedure,
            isAddOn: false, // This is a main charge (threshold-based service)
            healthcareService,
        },
        transaction,
    );

    // AFTER creating the main charge item, check for add-on triggers
    // Always use healthcareService for add-on triggers, not the charge object
    transaction = await processAddOnCodes({ healthcareService, encounterDurationMinutes, units, threshold, references, patientId: references.patient.id }, data, transaction);

    return transaction;
};

// Update the processFixedTimeDuration function to use participantEncounters when available
const processFixedTimeDuration = async function (data, healthcareService, references, relatedProcedure, charge, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Check if this is a group encounter
    if (data.isGroupEncounter) {
        // Check if this service has already been processed for the group
        const serviceKey = `${healthcareService.id}-${charge?.billingCode || healthcareService.billingCode}`;
        if (data.processedGroupServices && data.processedGroupServices.has(serviceKey)) {
            return transaction;
        }

        // Mark this service as processed for group encounters
        if (data.processedGroupServices) {
            data.processedGroupServices.add(serviceKey);
        }

        // Use the helper function to process group encounter participants
        await processGroupEncounterParticipants(data, async (patient, encounter, patientId) => {
            // Find patient procedure using helper function
            let patientProcedure = findPatientProcedure(data, patientId, healthcareService.billingCode, encounter);

            // If not found in memory, fetch from database
            if (!patientProcedure) {
                patientProcedure = await fetchPatientProcedureFromDB(data, patientId, healthcareService.billingCode);
            }

            // Create patient-specific references using helper function
            const patientReferences = createPatientSpecificReferences(references, patient, encounter);

            // Check if we have updated participant encounter information
            const updatedEncounter = data.participantEncounters[patientId];

            // Calculate patient-specific encounter duration using helper function
            let encounterToUse = updatedEncounter || encounter;
            let patientEncounterDurationMinutes = calculateEncounterDuration(encounterToUse);

            // Use helper function to calculate coverage and units
            const { calculatedUnits, patientCoverage } = await calculatePatientCoverageAndUnits(patientId, patientEncounterDurationMinutes, charge, healthcareService);
            // Update procedure units if needed using helper function
            await updateProcedureIfNeeded(patientProcedure, null, calculatedUnits, transaction);

            // Create charge data (only fields used by createAndSaveChargeItem)
            const perUnitCharge = getPerUnitChargeValue(charge, healthcareService, 0);
            const chargeData = {
                code: healthcareService.billingCode,
                price: perUnitCharge,
                status: 'planned',
                dateOfService: new Date().toISOString(),
                coverage: patientCoverage
                    ? {
                          id: patientCoverage.id,
                          resourceType: 'Coverage',
                          display: patientCoverage.payor?.[0]?.display || 'Unknown Payer',
                      }
                    : undefined,
            };

            // Create and save the charge item
            transaction = await createAndSaveChargeItem({ chargeData, references: patientReferences, relatedProcedure: patientProcedure, isAddOn: false, healthcareService }, transaction);

            // Process add-on codes using helper function
            // Always use healthcareService for add-on triggers, not the charge object
            transaction = await processAddOnCodes(
                { healthcareService, encounterDurationMinutes: patientEncounterDurationMinutes, units: calculatedUnits, threshold: null, references: patientReferences, patientId },
                data,
                transaction,
            );
        });
    } else {
        // Original single-patient logic for non-group encounters
        // Use helper function to calculate coverage and units
        const { calculatedUnits, maxUnitAllowed, patientCoverage } = await calculatePatientCoverageAndUnits(references.patient.id, data.encounterDurationMinutes, charge, healthcareService);

        // Only update the procedure if calculated units differ from current units
        if (relatedProcedure?.id && calculatedUnits !== relatedProcedure.unit) {
            await updateProcedureIfNeeded(relatedProcedure, null, calculatedUnits, transaction);
        }

        // FIRST: Create and save the main charge item
        const perUnitCharge = getPerUnitChargeValue(charge, healthcareService, 0);

        transaction = await createAndSaveChargeItem(
            {
                chargeData: {
                    code: healthcareService.billingCode,
                    display: healthcareService.name,
                    price: perUnitCharge,
                    status: 'planned',
                    dateOfService: new Date().toISOString(),
                    coverage: patientCoverage
                        ? {
                              id: patientCoverage.id,
                              resourceType: 'Coverage',
                              display: patientCoverage.payor?.[0]?.display || 'Unknown Payer',
                          }
                        : undefined,
                },
                references,
                relatedProcedure,
                isAddOn: null,
                healthcareService,
            },
            transaction,
        );

        // AFTER the main charge item has been created, check for add-on triggers
        // Always use healthcareService for add-on triggers, not the charge object
        transaction = await processAddOnCodes(
            { healthcareService, encounterDurationMinutes: data.encounterDurationMinutes, units: calculatedUnits, threshold: null, references, patientId: references.patient.id },
            data,
            transaction,
        );
    }

    return transaction;
};

const processThresholds = async function (data, healthcareService, references, relatedProcedure, charge, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // First prioritize charge thresholds, fall back to healthcareService thresholds
    const sourceThresholds = charge?.thresholds || healthcareService.thresholds;

    // Sort thresholds by min value for proper processing
    const sortedThresholds = [...sourceThresholds].sort((a, b) => (a.min || 0) - (b.min || 0));

    // Check if this is a group encounter
    if (data.isGroupEncounter) {
        // Check if this service has already been processed for the group
        const serviceKey = `${healthcareService.id}-${charge?.billingCode || healthcareService.billingCode}`;
        if (data.processedGroupServices && data.processedGroupServices.has(serviceKey)) {
            return transaction;
        }

        // Mark this service as processed for group encounters
        if (data.processedGroupServices) {
            data.processedGroupServices.add(serviceKey);
        }

        // Use the helper function to process group encounter participants
        await processGroupEncounterParticipants(data, async (patient, encounter, patientId) => {
            // Calculate patient-specific encounter duration using helper function
            let patientEncounterDurationMinutes = calculateEncounterDuration(encounter);

            // Find patient-specific procedure matching the billing code
            const procedureCodeOverride =
                charge?.thresholds?.find((t) => t.min === sortedThresholds[0]?.min)?.procedureCodeOverride ||
                healthcareService.thresholds?.find((t) => t.min === sortedThresholds[0]?.min)?.procedureCodeOverride;
            const billingCode = procedureCodeOverride || healthcareService.billingCode;

            const patientProcedure = findPatientProcedure(data, patientId, billingCode, encounter);

            // Create patient-specific references using helper function
            const patientReferences = createPatientSpecificReferences(references, patient, encounter);

            // Determine which threshold to use based on this patient's encounter duration using helper function
            let patientSelectedThreshold = findThresholdForDuration(sortedThresholds, patientEncounterDurationMinutes);

            // Process the selected threshold or all thresholds for this patient
            if (patientSelectedThreshold) {
                transaction = await createThresholdChargeItem(
                    {
                        threshold: patientSelectedThreshold,
                        healthcareService,
                        charge,
                        relatedProcedure: patientProcedure,
                        references: patientReferences,
                        encounterDurationMinutes: patientEncounterDurationMinutes,
                    },
                    data,
                    transaction,
                );
            } else {
                // Fallback: process each threshold if no duration or thresholds available
                for (const threshold of sortedThresholds) {
                    transaction = await createThresholdChargeItem(
                        { threshold, healthcareService, charge, relatedProcedure: patientProcedure, references: patientReferences, encounterDurationMinutes: patientEncounterDurationMinutes },
                        data,
                        transaction,
                    );
                }
            }
        });
    } else {
        // Single-patient logic- Determine which threshold to use based on encounter duration using helper function
        let selectedThreshold = findThresholdForDuration(sortedThresholds, data.encounterDurationMinutes);

        // Process the selected threshold or all thresholds if no selection
        if (selectedThreshold) {
            transaction = await createThresholdChargeItem(
                { threshold: selectedThreshold, healthcareService, charge, relatedProcedure, references, encounterDurationMinutes: data.encounterDurationMinutes },
                data,
                transaction,
            );
        } else {
            // Fallback: process each threshold
            for (const threshold of sortedThresholds) {
                transaction = await createThresholdChargeItem(
                    { threshold, healthcareService, charge, relatedProcedure, references, encounterDurationMinutes: data.encounterDurationMinutes },
                    data,
                    transaction,
                );
            }
        }
    }

    return transaction;
};

// CHARGE ITEM CREATION FUNCTIONS
const updateProcedureIfNeeded = async function (procedure, procedureCodeOverride, units, transaction) {
    if (!procedure?.id) return;

    let needsCodeUpdate = false;
    let needsUnitUpdate = false;

    // Check if procedure code needs update
    if (procedureCodeOverride && procedureCodeOverride !== procedure.code?.coding?.[0]?.code) {
        needsCodeUpdate = true;
    }

    // Check if units need update
    if (units !== procedure.unit) {
        needsUnitUpdate = true;
    }

    // Apply updates if needed
    if (needsCodeUpdate || needsUnitUpdate) {
        const updates = {};

        // Add code update if needed
        if (needsCodeUpdate && procedure.code?.coding?.length) {
            updates.code = {
                coding: [
                    {
                        code: procedureCodeOverride,
                        display: procedure.code.coding[0].display,
                    },
                ],
            };
        }

        // Add unit update if needed
        if (needsUnitUpdate) {
            updates.unit = units;
        }

        // Perform the update
        if (Object.keys(updates).length > 0) {
            // Update local object immediately (since we're using transactions)
            if (needsCodeUpdate && procedure.code?.coding?.length) {
                procedure.code.coding[0].code = procedureCodeOverride;
            }
            if (needsUnitUpdate) {
                procedure.unit = units;
            }

            // Add procedure update to transaction
            transaction.push(crud.patch(procedure));
        }
    }
};

const createProcedure = async function (chargeData, references, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Create the procedure using FHIR structure
    const procedureData = {
        resourceType: 'Procedure',
        status: 'completed',
        code: {
            coding: [
                {
                    code: chargeData.code,
                    display: chargeData.display,
                },
            ],
        },
        subject: references.patient,
        encounter: references.encounter,
        performer: [
            {
                actor: references.provider,
            },
        ],
        unit: chargeData.quantity,
    };

    // Create a plain object with resourceType and toJSON method for crud.post
    const procedureForCrud = {
        resourceType: 'Procedure',
        toJSON: () => procedureData,
    };

    // Add to transaction instead of direct API call
    const crudOperation = crud.post(procedureForCrud);
    transaction.push(crudOperation);

    // Create a procedure object with transaction reference for return
    const procedure = {
        ...procedureData,
        id: crudOperation.fullUrl, // Use the transaction's fullUrl reference
    };

    return { procedure, transaction };
};

const createAndSaveChargeItem = async function ({ chargeData, references, relatedProcedure, isAddOn = false, healthcareService = null }, existingTransaction = false) {
    let transaction = existingTransaction || [];
    // Only create procedure if it's not an add-on code and no related procedure exists
    // Add-on procedures should already be created by BnQuestionnaireFiller with partOf references
    if (!relatedProcedure && !isAddOn) {
        const result = await createProcedure(chargeData, references, transaction);
        transaction = result.transaction;
        relatedProcedure = result.procedure;
    }

    // Use the local procedure data since we're working with transactions
    // The procedure will be updated in the transaction if needed
    let latestProcedureData = relatedProcedure;

    // For add-on codes, always use 1 as the quantity
    // For regular charges, get the most accurate unit value
    const mostAccurateUnitValue = isAddOn ? 1 : latestProcedureData?.unit || relatedProcedure?.unit || chargeData.quantity;

    // First, check if a charge item with the same billing code, patient, and encounter already exists
    let existingChargeItems = [];
    await $httpFhirApi.get(`ChargeItem?code=${chargeData.code}&subject=${references.patient.id}&context=${references.encounter.id}`).then((existingChargeItemsResponse) => {
        if (existingChargeItemsResponse.data && existingChargeItemsResponse.data.entry && existingChargeItemsResponse.data.entry.length > 0) {
            existingChargeItems = existingChargeItemsResponse.data.entry.map((entry) => entry.resource);
        } else if (existingChargeItemsResponse.data && Array.isArray(existingChargeItemsResponse.data)) {
            existingChargeItems = existingChargeItemsResponse.data;
        } else if (existingChargeItemsResponse.data && existingChargeItemsResponse.data.resourceType === 'ChargeItem') {
            existingChargeItems = [existingChargeItemsResponse.data];
        }
    });

    if (existingChargeItems.length > 0) {
        // Instead of skipping, update the existing charge item with the new quantity
        const existingChargeItem = existingChargeItems[0]; // Use the first one if multiple exist

        // Update the quantity based on whether this is an add-on code or not
        if (existingChargeItem.quantity) {
            existingChargeItem.quantity.value = isAddOn ? 1 : mostAccurateUnitValue;
        } else {
            existingChargeItem.quantity = {
                value: isAddOn ? 1 : mostAccurateUnitValue,
                unit: 'units',
            };
        }

        // If procedure has updated service reference, update it in charge item too
        if (latestProcedureData?.id && !isAddOn) {
            existingChargeItem.service = [
                {
                    id: latestProcedureData.id,
                    resourceType: 'Procedure',
                },
            ];
        }

        let { getChargeItemAuths } = useAuthFlow();
        await getChargeItemAuths(references.patient, healthcareService, latestProcedureData, existingChargeItem);

        // Update the charge item in the database
        transaction.push(crud.put(existingChargeItem));
        return transaction;
    }

    // Create the charge item using our class
    const chargeItem = new ChargeItem({
        status: chargeData.status || 'planned',
        code: {
            coding: [
                {
                    code: chargeData.code,
                    display: chargeData.display,
                },
            ],
        },
        subject: references.patient,
        context: references.encounter,
        performer: [
            {
                actor: references.provider,
            },
        ],
        quantity: {
            value: isAddOn ? 1 : mostAccurateUnitValue,
            unit: 'units',
        },
        priceOverride: {
            value: chargeData.price,
            currency: 'USD',
        },
        coverage: chargeData.coverage,
    });

    // Set occurrence[x] using ValueX polymorphic setter
    chargeItem.occurrenceX = chargeData.dateOfService || new Date().toISOString();

    // Add reference to Procedure if available and not an add-on code
    if (latestProcedureData?.id && !isAddOn) {
        // Check if this is a transaction reference (starts with urn:uuid:)
        if (latestProcedureData.id.startsWith('urn:uuid:')) {
            // Use the transaction reference directly
            chargeItem.service = [
                new Reference({
                    resourceType: 'Procedure',
                    id: latestProcedureData.id,
                }),
            ];
        } else {
            // Use the regular resource reference
            chargeItem.service = [
                new Reference({
                    id: latestProcedureData.id,
                    resourceType: 'Procedure',
                }),
            ];
        }

        // Ensure the chargeItem code matches the procedure
        if (latestProcedureData.code?.coding?.length) {
            chargeItem.code = {
                coding: [
                    {
                        code: latestProcedureData.code.coding[0].code,
                        display: latestProcedureData.code.coding[0].display,
                    },
                ],
            };
        }
    }

    // Ensure the chargeItem quantity matches the procedure (regardless of add-on status)
    if (latestProcedureData?.unit) {
        chargeItem.quantity = {
            value: latestProcedureData.unit,
            unit: 'units',
        };
    }

    if (references.questionnaireResponse) {
        chargeItem.supportingInformation = [references.questionnaireResponse];
    } else {
        chargeItem.supportingInformation = [];
    }

    // Save the charge item - convert to JSON and set ID
    const chargeItemJson = chargeItem.toJSON();
    const chargeItemId = uuidv4();
    chargeItemJson.id = chargeItemId;

    // Create a plain object with resourceType and toJSON method for crud.post
    const chargeItemForCrud = {
        resourceType: 'ChargeItem',
        toJSON: () => chargeItemJson,
    };

    // Add to transaction instead of direct API call
    transaction.push(crud.post(chargeItemForCrud));

    let { getChargeItemAuths } = useAuthFlow();
    let patients = [references.patient];
    let services = [healthcareService];
    let procedures = [latestProcedureData];

    await getChargeItemAuths(patients, services, procedures, chargeItemJson, true);

    return transaction;
};

// DECISION LOGIC FUNCTIONS
const shouldProcessGroupEncounter = function (data) {
    const isGroup = data.isGroupEncounter && data.participantEncounters && Object.keys(data.participantEncounters).length > 0;
    return isGroup;
};

const shouldProcessWithCoverageFeeSchedule = async function (data, patientId) {
    if (!patientId) {
        return { shouldProcess: false, coverage: null, feeSchedule: null };
    }

    const patientCoverage = await fetchPatientCoverage(patientId);

    if (!patientCoverage) {
        return { shouldProcess: false, coverage: null, feeSchedule: null };
    }

    const coverageFeeSchedule = await fetchPatientFeeSchedule(patientCoverage);
    const shouldProcess = coverageFeeSchedule && coverageFeeSchedule.length > 0;

    return { shouldProcess, coverage: patientCoverage, feeSchedule: coverageFeeSchedule };
};
const processGroupEncounterParticipants = async function (data, processPatientCallback) {
    // Keep track of processed participants to avoid duplicates
    const processedParticipantIds = new Set();

    // Process each participant in the group using participantEncounters
    for (const patientId in data.participantEncounters) {
        // Skip if we've already processed this patient
        if (processedParticipantIds.has(patientId)) {
            continue;
        }

        // Mark patient as processed
        processedParticipantIds.add(patientId);

        const patient = data.patients.find((p) => p.id === patientId);
        if (!patient) {
            continue;
        }

        // Get the corresponding encounter for this patient
        const encounter = data.groupEncounters.find((enc) => enc.subject?.id === patientId);
        if (!encounter) {
            continue;
        }

        // Call the provided callback function to process this patient
        await processPatientCallback(patient, encounter, patientId);
    }
};

const calculatePatientCoverageAndUnits = async function (patientId, encounterDurationMinutes, charge, healthcareService) {
    const patientCoverage = await fetchPatientCoverage(patientId);
    const hasCoverage = !!patientCoverage;
    // Get per unit duration and max units based on coverage
    const { perUnitDuration, maxUnitAllowed } = getPerUnitDurationAndMaxUnits(hasCoverage, charge, healthcareService);
    // Calculate units based on duration
    const calculatedUnits = calculateUnitsForDuration(encounterDurationMinutes, perUnitDuration, maxUnitAllowed);

    return { patientCoverage, hasCoverage, perUnitDuration, maxUnitAllowed, calculatedUnits };
};

const findPatientProcedure = function (data, patientId, billingCode, encounter) {
    // First try to find procedure in memory
    let patientProcedure = null;
    if (data.procedures && Array.isArray(data.procedures)) {
        patientProcedure = data.procedures.find(
            (proc) => proc.subject?.id === patientId && proc.code?.coding?.some((coding) => coding.code === billingCode) && (proc.encounter?.id === encounter.id || proc.context?.id === encounter.id),
        );
    }

    return patientProcedure;
};
// MAIN FUNCTION
const processChargeItemFlow = async function (data, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Validate required parameters (following codebase pattern)
    if (!data?.actor || !data?.activeEncounter) {
        return transaction;
    }

    // Track which patients have already had add-on triggers checked to prevent duplicates
    const addOnTriggersChecked = new Set();
    // Track which services have already been processed in group encounters to prevent duplicates
    const processedGroupServices = new Set();

    // Add the wrapper function to the data object for easy access throughout the call chain
    data.checkForAddOnTriggersOnce = async (patientId, service, durationMinutes, calculatedUnits, selectedThreshold) => {
        const key = `${patientId}-${service.id}`;
        if (addOnTriggersChecked.has(key)) {
            return null;
        }

        addOnTriggersChecked.add(key);
        return await data.checkForAddOnTriggers(service, durationMinutes, calculatedUnits, selectedThreshold);
    };
    // Add group service tracking to data object
    data.processedGroupServices = processedGroupServices;

    // Use decision functions to determine processing approach
    if (shouldProcessGroupEncounter(data)) {
        // Keep track of processed patients to avoid duplicates
        const processedPatientIds = new Set();

        // Process each patient in the participantEncounters
        for (const patientId in data.participantEncounters) {
            if (processedPatientIds.has(patientId)) continue;
            processedPatientIds.add(patientId);

            // Find the patient
            const patient = data.patients.find((p) => p.id === patientId);
            if (!patient) {
                continue;
            }

            // Use decision function to check if patient has coverage with fee schedule
            const { shouldProcess: shouldProcessWithFeeSchedule, feeSchedule: patientFeeSchedule } = await shouldProcessWithCoverageFeeSchedule(data, patientId);

            if (shouldProcessWithFeeSchedule && patientFeeSchedule) {
                // Process using the patient's fee schedule
                const result = await processPatientFeeSchedule(data, patientId, patientFeeSchedule, transaction);
                transaction = result.transaction;

                // Only fall back to healthcare services if fee schedule actually created no charge items
                // AND we have updatedServices to process
                if (result.chargesProcessed === 0 && data.updatedServices?.length > 0) {
                    transaction = await processPatientHealthcareServices(data, patientId, transaction);
                }
            } else {
                // No fee schedule but has coverage, or no coverage - use healthcare services
                transaction = await processPatientHealthcareServices(data, patientId, transaction);
            }
        }
    } else {
        // For single patient encounters, use the original approach
        const patientId = data.activePatient?.id;
        let patientProcessed = false;

        if (patientId) {
            // Use decision function to check if patient has coverage with fee schedule
            const { shouldProcess: shouldProcessWithFeeSchedule, feeSchedule: coverageFeeSchedule } = await shouldProcessWithCoverageFeeSchedule(data, patientId);

            if (shouldProcessWithFeeSchedule && coverageFeeSchedule) {
                // Process using the coverage's feeSchedule instead of healthcareService
                const result = await processFeeSchedules(data, coverageFeeSchedule, transaction);
                transaction = result.transaction;

                // Only mark as processed if fee schedule actually created charge items
                if (result.chargesProcessed > 0) {
                    patientProcessed = true;
                }
            }

            // If patient wasn't processed through coverage feeSchedule, check if we have other feeSchedule data
            if (!patientProcessed && data.feeScheduleData?.length > 0) {
                // Use existing feeSchedule if available
                const result = await processFeeSchedules(data, data.feeScheduleData, transaction);
                transaction = result.transaction;

                // Only mark as processed if fee schedule actually created charge items
                if (result.chargesProcessed > 0) {
                    patientProcessed = true;
                }
            }

            // Only process through healthcareService if patient hasn't been processed through any feeSchedule
            if (!patientProcessed && data.updatedServices?.length > 0) {
                // Fall back to healthcare services
                transaction = await processHealthcareServices(data, data.updatedServices, transaction);
            }
        } else {
            // No patient ID - cannot process without a patient
            if (existingTransaction === false) {
                return await postTransaction(transaction);
            }
            return transaction;
        }
    }

    // Execute the transaction if this is not an existing transaction
    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        return result;
    }

    return transaction;
};

const removeFormChargeItems = async function (chargeItemIds, existingTransaction = false) {
    let transaction = existingTransaction || [];

    // Delete each ChargeItem
    for (const chargeItemId of chargeItemIds) {
        transaction.push(crud.delete({ id: chargeItemId, resourceType: 'ChargeItem' }));
    }

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

export function useChargeItemFlow() {
    return {
        processChargeItemFlow,
        removeFormChargeItems,
        fetchPatientCoverage,
    };
}
